/**
 * What is coming up: the ring read forwards from today, as a short list.
 *
 * The ring answers "when in the year?" at a glance and "what is next?" only by
 * finding the today hand and reading clockwise. This is that reading, done
 * once — the next few entries in the order they arrive, with the day they land
 * on and the age being turned.
 *
 * Pure: no DOM, no clock of its own. `today` is given so the dock and a test
 * ask the same question.
 */
import { effectiveMonthDay, type CalEvent } from '@circle/shared';
import { ageIn, formatDayMonth } from '../core/format';

export interface UpcomingRow {
  event: CalEvent;
  /** The calendar year the occurrence falls in: the ring's, or the one after. */
  year: number;
  /** Where it lands, folded the way the ring folds it (Feb 29 → Feb 28). */
  month: number;
  day: number;
  /** 0 for today, 1 for tomorrow, and never more than a year ahead. */
  daysUntil: number;
  /** The age turned on that date, for a birthday that knows its birth year. */
  age: number | undefined;
}

export interface UpcomingOptions {
  /** The year the ring is showing. */
  year: number;
  /** Anything with a date; only the day of it is read. */
  today?: Date;
  /** The search and the circle filter, already composed into one question. */
  keep?: (event: CalEvent) => boolean;
}

const DAY_MS = 86_400_000;

/**
 * The ring's entries, soonest first.
 *
 * The window is the twelve months from today, not the calendar year: on the
 * 28th of December the next four birthdays are mostly January's, and a list
 * that stopped at the 31st would be a list of one. An entry whose date has
 * gone by is therefore dated in the **next** year, which is also the year its
 * age is counted in — "turns 41" rather than the 40 the ring is showing.
 *
 * Looking at a year that is not this one has no "today" in it, so the reading
 * starts at its 1st of January and the whole year is what is coming up.
 */
export function upcoming(events: readonly CalEvent[], options: UpcomingOptions): UpcomingRow[] {
  const { year, keep } = options;
  const today = options.today ?? new Date();
  const from =
    today.getFullYear() === year
      ? { month: today.getMonth() + 1, day: today.getDate() }
      : { month: 1, day: 1 };
  const start = Date.UTC(year, from.month - 1, from.day);

  const rows: UpcomingRow[] = [];
  for (const event of events) {
    if (keep && !keep(event)) continue;
    const here = effectiveMonthDay(year, event.month, event.day);
    const wraps = here.month < from.month || (here.month === from.month && here.day < from.day);
    const occurrenceYear = wraps ? year + 1 : year;
    // A note pinned to one year happens once; it is not coming up in any other.
    if (event.kind === 'note' && event.year !== undefined && event.year !== occurrenceYear) continue;
    const at = wraps ? effectiveMonthDay(occurrenceYear, event.month, event.day) : here;
    const age = ageIn(event, occurrenceYear);
    rows.push({
      event,
      year: occurrenceYear,
      month: at.month,
      day: at.day,
      daysUntil: Math.round((Date.UTC(occurrenceYear, at.month - 1, at.day) - start) / DAY_MS),
      age: age === undefined || age < 0 ? undefined : age,
    });
  }

  return rows.sort(
    (a, b) =>
      a.daysUntil - b.daysUntil ||
      a.event.title.localeCompare(b.event.title) ||
      (a.event.id < b.event.id ? -1 : a.event.id > b.event.id ? 1 : 0),
  );
}

/**
 * The muted half of a row: "12 Mar · turns 40".
 *
 * The next two days are named rather than dated, because "Today" is the one
 * thing in the list somebody has to act on and a date makes them work it out.
 */
export function upcomingWhen(row: UpcomingRow): string {
  const when =
    row.daysUntil === 0
      ? 'Today'
      : row.daysUntil === 1
        ? 'Tomorrow'
        : formatDayMonth(row.month, row.day);
  return row.age === undefined ? when : `${when} · turns ${row.age}`;
}
